import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, ShieldAlert, CheckCircle2, Globe2, Clock } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public security-alert landing page.
 * Reached from the "Sign-in from new country" email's two buttons:
 *   /security/login-alert/:token?action=ack   — trust this country
 *   /security/login-alert/:token?action=deny  — not me → reset password
 *
 * Flow:
 *   1. GET /api/login-alert/verify/:token — renders country/IP/timestamp
 *   2. If ?action= is present, auto-fires the corresponding POST
 *   3. Otherwise shows two big buttons and lets the user pick
 */
export default function LoginAlert() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preferredAction = searchParams.get("action"); // "ack" | "deny" | null

  const [phase, setPhase] = useState("loading"); // loading | ready | acking | denying | acked | denied | error
  const [info, setInfo] = useState(null);
  const [resetUrl, setResetUrl] = useState("");
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/login-alert/verify/${token}`);
        setInfo(data);
        if (preferredAction === "ack") {
          await runAck();
        } else if (preferredAction === "deny") {
          await runDeny();
        } else {
          setPhase("ready");
        }
      } catch (err) {
        setErrorText(err.response?.data?.detail || "This security-alert link has expired.");
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const runAck = async () => {
    setPhase("acking");
    try {
      await axios.post(`${API_BASE}/api/login-alert/ack/${token}`);
      setPhase("acked");
    } catch (err) {
      setErrorText(err.response?.data?.detail || "Could not record your choice.");
      setPhase("error");
    }
  };

  const runDeny = async () => {
    setPhase("denying");
    try {
      const { data } = await axios.post(`${API_BASE}/api/login-alert/deny/${token}`);
      setResetUrl(data.reset_url || "");
      setPhase("denied");
    } catch (err) {
      setErrorText(err.response?.data?.detail || "Could not record your choice.");
      setPhase("error");
    }
  };

  if (phase === "loading") {
    return (
      <Shell>
        <Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto" data-testid="loginalert-loading" />
      </Shell>
    );
  }

  if (phase === "error") {
    return (
      <Shell>
        <div className="text-center" data-testid="loginalert-error">
          <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
            Link expired
          </h1>
          <p className="text-sm text-slate-600">{errorText}</p>
        </div>
      </Shell>
    );
  }

  if (phase === "acked") {
    return (
      <Shell>
        <div className="text-center" data-testid="loginalert-acked">
          <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-6 h-6 text-emerald-700" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
            Thanks — marked as trusted
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            We'll stop emailing you about sign-ins from <strong>{info?.country}</strong>. Future logins from a different country will still trigger an alert.
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === "denied") {
    return (
      <Shell>
        <div className="text-center" data-testid="loginalert-denied">
          <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
            Account secured — reset your password now
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed mb-5">
            We've issued a single-use password-reset link valid for 60 minutes. Tap the button below to choose a new password.
          </p>
          {resetUrl && (
            <a
              href={resetUrl}
              data-testid="loginalert-reset-cta"
              className="inline-block bg-red-700 hover:bg-red-800 text-white text-base font-bold py-3 px-8 rounded-sm"
            >
              Reset my password →
            </a>
          )}
        </div>
      </Shell>
    );
  }

  // phase === "ready" | "acking" | "denying"
  return (
    <Shell>
      <div data-testid="loginalert-choice">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-amber-700" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>
            Was this you?
          </h1>
          <p className="text-sm text-slate-600">
            We detected a sign-in to {info?.user_email} from a new country.
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm space-y-2 mb-5">
          <Row icon={Globe2} label="Country" value={info?.country} testid="loginalert-country" />
          <Row icon={Globe2} label="IP address" value={info?.ip} mono testid="loginalert-ip" />
          <Row icon={Clock} label="Time" value={(info?.at || "").replace("T", " ").slice(0, 19) + " UTC"} testid="loginalert-time" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={runAck}
            disabled={phase !== "ready"}
            data-testid="loginalert-ack-button"
            className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold py-3 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {phase === "acking" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Yes, it was me
          </button>
          <button
            type="button"
            onClick={runDeny}
            disabled={phase !== "ready"}
            data-testid="loginalert-deny-button"
            className="flex-1 bg-red-700 hover:bg-red-800 text-white text-sm font-bold py-3 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {phase === "denying" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
            No, this wasn't me
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-6 sm:p-8">
        {children}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono, testid }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
      <div className="text-xs text-slate-500 w-20 shrink-0">{label}</div>
      <div className={`text-sm text-slate-900 font-semibold ${mono ? "font-mono" : ""}`} data-testid={testid}>
        {value || "—"}
      </div>
    </div>
  );
}
